// @generated-imports
import { buildAbsoluteUrl, escapeHTML } from './app.js';
import { CONFIG, dom, state } from './core.js';
import { copyText } from './diagnostics.js';
import { formatBytes } from './format.js';
import { gpShortName } from './gamepad.js';
import { layerClosed, layerOpened } from './layers.js';
import { connectedPads } from './multiplayer.js';
import { listEmulatorBuilds, resolveEmulatorBuild } from './shared-config.js';
// @generated-imports
/* ===========================================================================
   VERIFICAR ESTE APARELHO (v2.1)
   ---------------------------------------------------------------------------
   Autodiagnóstico ANTES do erro: roda as verificações que costumam explicar
   falhas (threads, WebGL, WebAssembly, armazenamento, service worker,
   controles e o alcance das duas builds do emulador e de uma ROM). Serve
   para configurar uma TV box nova e para conferir uma publicação.
   ======================================================================== */

async function headOk(url) {
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    return { ok: response.ok, status: response.status };
  } catch (error) {
    return { ok: false, status: 0, error: String((error && error.message) || error) };
  }
}

async function runDeviceChecks(onProgress) {
  const results = [];
  const push = (label, status, detail) => {
    results.push({ label, status, detail });
    if (typeof onProgress === 'function') onProgress(results.slice());
  };

  push('Conexão', navigator.onLine === false ? 'warn' : 'ok', navigator.onLine === false ? 'Offline — só jogos marcados como offline abrem.' : 'Online.');
  push('Threads do emulador', window.crossOriginIsolated ? 'ok' : 'warn', window.crossOriginIsolated
    ? 'Isolamento de origem ativo (COOP/COEP): o emulador pode usar threads.'
    : 'Sem isolamento de origem: funciona, mas sem threads. Configure COOP/COEP no servidor (README).');
  push('WebAssembly', typeof WebAssembly === 'object' ? 'ok' : 'fail', typeof WebAssembly === 'object' ? 'Disponível.' : 'Indisponível — os núcleos do emulador não rodam neste navegador.');
  let gl = null;
  try {
    const canvas = document.createElement('canvas');
    gl = canvas.getContext && (canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch (_) { gl = null; }
  push('Vídeo (WebGL)', gl ? 'ok' : 'warn', gl ? 'Aceleração gráfica disponível.' : 'Sem WebGL — o vídeo pode ficar lento; experimente o modo leve.');

  try {
    await new Promise((resolve, reject) => {
      const request = indexedDB.open('emutab-check', 1);
      request.onsuccess = () => { request.result.close(); indexedDB.deleteDatabase('emutab-check'); resolve(); };
      request.onerror = () => reject(request.error);
    });
    push('Banco local (IndexedDB)', 'ok', 'Saves, estados e biblioteca podem ser guardados.');
  } catch (_) {
    push('Banco local (IndexedDB)', 'fail', 'Indisponível (janela anônima ou bloqueio do navegador): nada será guardado.');
  }

  try {
    const estimate = navigator.storage && navigator.storage.estimate ? await navigator.storage.estimate() : null;
    if (estimate && estimate.quota) {
      const pct = Math.round(((estimate.usage || 0) / estimate.quota) * 100);
      push('Espaço', pct > 80 ? 'warn' : 'ok', `${formatBytes(estimate.usage || 0)} de ${formatBytes(estimate.quota)} usados (${pct}%).`);
    } else {
      push('Espaço', 'warn', 'O navegador não informa a cota.');
    }
    const persisted = navigator.storage && navigator.storage.persisted ? await navigator.storage.persisted() : false;
    push('Armazenamento persistente', persisted ? 'ok' : 'warn', persisted ? 'O navegador não apaga seus dados sozinho.' : 'Pode ser limpo pelo navegador sob pouco espaço — exporte backups.');
  } catch (_) {
    push('Espaço', 'warn', 'Não foi possível consultar.');
  }

  const sw = navigator.serviceWorker && navigator.serviceWorker.controller;
  push('App instalado / offline', sw ? 'ok' : 'warn', sw ? 'Service worker ativo.' : 'Service worker ainda não controla a página (primeira visita ou HTTP sem TLS).');

  const pads = connectedPads();
  push('Controles', pads.length ? 'ok' : 'warn', pads.length ? `${pads.length} conectado(s): ${pads.map((p) => gpShortName(p.id)).join(', ')}.` : 'Nenhum detectado — aperte um botão do controle.');

  for (const id of listEmulatorBuilds()) {
    const build = resolveEmulatorBuild(id);
    // eslint-disable-next-line no-await-in-loop
    const res = await headOk(`${build.dataPath}loader.js`);
    const active = id === CONFIG.emulatorBuild;
    push(`Emulador "${id}"${active ? ' (em uso)' : ''}`, res.ok ? 'ok' : (active ? 'fail' : 'warn'),
      res.ok ? 'loader.js acessível.' : `loader.js inacessível (${res.status || res.error || 'erro'}).`);
  }

  const sample = (state.catalog || [])[0];
  if (sample) {
    // eslint-disable-next-line no-await-in-loop
    const res = await headOk(buildAbsoluteUrl(sample.romUrl));
    push('ROM de exemplo', res.ok ? 'ok' : 'fail', res.ok ? `"${sample.title}" acessível.` : `"${sample.title}" inacessível (${res.status || res.error || 'erro'}).`);
  }

  push('Tela', 'ok', `${window.innerWidth}×${window.innerHeight} px · densidade ${window.devicePixelRatio || 1} · interface ${document.documentElement.dataset.uiScale || 100}%.`);
  return results;
}

function renderDeviceChecks(results, done) {
  if (!dom.deviceCheckList) return;
  const icon = { ok: '✅', warn: '⚠️', fail: '❌' };
  dom.deviceCheckList.innerHTML = results.map((r) => `<div class="backup-row data-row check-row is-${r.status}">
      <span class="data-info"><strong>${icon[r.status]} ${escapeHTML(r.label)}</strong> <small>${escapeHTML(r.detail)}</small></span>
    </div>`).join('') + (done ? '' : '<div class="backup-row">Verificando…</div>');
}

function deviceCheckReport(results) {
  return [`EmuTab ${CONFIG.appVersion} — verificação do aparelho`, `Data: ${new Date().toISOString()}`, `Navegador: ${navigator.userAgent}`]
    .concat(results.map((r) => `[${r.status}] ${r.label}: ${r.detail}`)).join('\n');
}

async function openDeviceCheck() {
  if (!dom.deviceCheckModal) return;
  dom.deviceCheckModal.classList.remove('is-hidden');
  layerOpened('device-check', dom.deviceCheckClose);
  renderDeviceChecks([], false);
  const results = await runDeviceChecks((partial) => renderDeviceChecks(partial, false));
  state.lastDeviceCheck = results;
  renderDeviceChecks(results, true);
  return results;
}

function closeDeviceCheck() {
  if (!dom.deviceCheckModal) return;
  dom.deviceCheckModal.classList.add('is-hidden');
  layerClosed('device-check');
}

function copyDeviceCheck() {
  if (!state.lastDeviceCheck) return;
  copyText(deviceCheckReport(state.lastDeviceCheck), 'Relatório copiado.');
}

// @generated-exports
export { closeDeviceCheck, copyDeviceCheck, deviceCheckReport, headOk, openDeviceCheck, renderDeviceChecks, runDeviceChecks };
// @generated-exports
